import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Box, Button, CircularProgress, Divider, IconButton, InputAdornment, Modal, TextField, Typography, debounce, styled, useTheme } from '@mui/material';
import { Check, Close, Search } from '@mui/icons-material';

import { HotelResponseDTO } from '@bandido/shared/dtos';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { Table, TableLoader } from '@frontend/features/common/components/Table';
import { QueryKeys } from '@frontend/features/common/types';
import {
  useGetHotelDupesManual,
  useGetHotelDupesTentative,
  useLinkHotelMutation,
  useUnlinkHotelMutation,
  useGetHotelData,
} from '../../queries';

import { useHotelMetadata } from '../../hooks';
import { useLoader } from './Loader';


const ID_KEY = 'linked-hotels-editor';

interface LinkedHotelsModalProps {
  open: boolean;
  onClose: () => void;
}

const StyledBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  height: '90%',
  maxHeight: '90%',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2)
}));

export const LinkedHotelsModal: FC<LinkedHotelsModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const { hotelId } = useParams();
  const {
    isLoading: loadingHotel,
    data: hotel
  } = useGetHotelData({
    hotelId: `${hotelId}`,
  });
  const { toggleLoader } = useLoader();
  const [manualHotelSearch, setManualHotelSearch] = useState('');
  const [manualHotelSearchValue, setManualHotelSearchValue] = useState('');

  const { isLoading: loadingMeta, findSupplier } = useHotelMetadata();
  const { isLoading: loadingData, data: dupesData } = useGetHotelDupesTentative(hotel?.id);
  const { isLoading: loadingManualHotelSearch, data: manualHotelSearchData} = useGetHotelDupesManual({
    freeText: manualHotelSearchValue
  });


  const moderated = useMemo(
    () => dupesData?.data.filter(({ modParent }) => +modParent === hotel?.id) || [],
    [dupesData, hotel],
  );

  const { mutate: linkHotel } = useLinkHotelMutation({
    onSuccess: () => {
      addToast({
        color: 'success',
        message: t('hotels.contentManager.detail.moderation.link.success'),
      });
      queryClient.invalidateQueries([QueryKeys.getHotelDupesTentative]);
      queryClient.invalidateQueries([QueryKeys.getHotelModeratedBy, hotelId]);
      queryClient.invalidateQueries([QueryKeys.getHotelCardData, hotelId]);
      queryClient.invalidateQueries([QueryKeys.getHotelData, hotelId]);
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('hotels.contentManager.detail.moderation.link.error'),
      });
    },
    onSettled: () => {
      toggleLoader(ID_KEY, false);
    }
  });

  const { mutate: unlinkHotel } = useUnlinkHotelMutation({
    onSuccess: () => {
      addToast({
        color: 'success',
        message: t('hotels.contentManager.detail.moderation.unlink.success'),
      });
      queryClient.invalidateQueries([QueryKeys.getHotelDupesTentative]);
      queryClient.invalidateQueries([QueryKeys.getHotelModeratedBy, hotelId]);
      queryClient.invalidateQueries([QueryKeys.getHotelCardData, hotelId]);
      queryClient.invalidateQueries([QueryKeys.getHotelData, hotelId]);
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('hotels.contentManager.detail.moderation.unlink.error'),
      });
    },
    onSettled: () => {
      toggleLoader(ID_KEY, false);
    }
  });

  const handleAction = (hotelToLink: HotelResponseDTO) => {
    if (hotel) {
      toggleLoader(ID_KEY, true);
      if (+hotelToLink.modParent === hotel.id && moderated.length > 1) {
        unlinkHotel({
          hotelId: hotelToLink.id,
        });
      } else {
        linkHotel({
          hotelId: hotelToLink.id,
          modParentId: hotel.id,
        });
      }
    }
  };

  const handleClose = () => {
    setManualHotelSearch('');
    onClose();
  };

  const updateFreeTextSearch = useCallback(
    debounce((value: string) => {
      setManualHotelSearchValue(value);
    }, 500),
    [setManualHotelSearchValue]
  );

  const freeTextEndAdornment = useMemo(() => (
    <InputAdornment position="end">
      {
        Boolean(!loadingManualHotelSearch && manualHotelSearchValue || !!manualHotelSearchData?.data.length) && (
          <IconButton onClick={() => setManualHotelSearch('')}>
            {<Close />}
          </IconButton>
        )
      }
      {
        loadingManualHotelSearch && (
          <CircularProgress sx={{ width: 24, height: 24 }}/>
        )
      }
    </InputAdornment>
  ), [loadingManualHotelSearch, manualHotelSearchValue, !!manualHotelSearchData?.data.length]);
    
  useEffect(() => {
    updateFreeTextSearch(manualHotelSearch);
    return () => updateFreeTextSearch.clear();
  }, [manualHotelSearch, updateFreeTextSearch]);

  useEffect(() => {
    toggleLoader(ID_KEY, loadingMeta || loadingData || loadingHotel);
  }, [loadingMeta, loadingData, loadingHotel]);


  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby={t('hotels.hotelImageTag.delete.title')}
    >
      <StyledBox>
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <Typography id="delete-image-tag-modal-title" variant="h6" component="h2">
              {t('hotels.contentManager.detail.moderation.link.title')}
              {hotel && ` - ${hotel.name} (ID: ${hotel.id})`}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <Close color="primary" />
          </IconButton>
        </Box>
        <Divider sx={{ my: theme.spacing(3) }} />
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="subtitle1" minWidth={130} component="div">
            {t('hotels.contentManager.detail.moderation.link.manually')}
          </Typography>
          <TextField
            size="small"
            placeholder={t('hotels.contentManager.search')}
            inputProps={{
              'aria-label': t('hotels.contentManager.search'),
            }}
            onChange={(e) => setManualHotelSearch(e.target.value)}
            value={manualHotelSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: freeTextEndAdornment
            }}
            sx={{
              width: 200
            }}
          />
        </Box>
        {
          !loadingManualHotelSearch && hotel && manualHotelSearchValue && !!manualHotelSearchData?.data && (
            <>
              {manualHotelSearchData.data.map((hotelResult) => (
                <Box key={hotelResult.id} display="flex" alignItems="center" gap={2} my={theme.spacing(1)}>
                  {Number(hotelResult.modParent) === Number(hotel.id) ? (
                    <Button
                      sx={{ minWidth: 120 }}
                      variant='outlined'
                      color='secondary'
                      size='small'
                      disabled
                    >
                      {t('hotels.contentManager.detail.moderation.linked.title')}
                    </Button>
                  ) : (
                    <Button
                      sx={{ minWidth: 120 }} 
                      variant='contained' 
                      color='primary' 
                      size='small'
                      onClick={() => handleAction(hotelResult)}
                    >
                      {t('hotels.contentManager.detail.moderation.link.button')}
                    </Button>
                  )}
                  <Typography variant="body2" minWidth={130} component="div">
                    {
                      // eslint-disable-next-line max-len
                      `${hotelResult.name} (${hotelResult.country}) - ${findSupplier(hotelResult.supplierId)?.supplierName}`
                    }
                  </Typography>
                </Box>
              ))}
            </>
          )
        }

        <Divider sx={{ my: theme.spacing(3) }} />
        <Box sx={{
          height: `calc(90% - ${theme.spacing(4)})`,
          overflowY: 'auto',
        }}  >
          {loadingMeta || loadingData && (
            <TableLoader
              sx={{
                backgroundColor: theme.palette.field.main,
                paddingY: theme.spacing(2),
              }}
              rows={3}
            />
          )}
          {hotel && dupesData && dupesData.data.length > 0 && (
            <Table
              stickyHeader
              columns={[

                {
                  id: 'linked',
                  label: t('hotels.contentManager.fields.linked'),
                  align: 'left',
                  format: (_value, option) => (+option.modParent === hotel.id ? <Check color="success" /> : <Close color="error" />),
                },
                {
                  id: '_',
                  label: t('common.fields.actions'),
                  align: 'left',
                  format: (_value, displayHotel) => {
                    if (+displayHotel.modParent === hotel.id) {
                      return (
                        <Button
                          variant="text"
                          size='small'
                          disabled={moderated.length <= 1}
                          onClick={() => handleAction(displayHotel)}
                        >
                          {t('hotels.contentManager.detail.moderation.unlink.button')}
                        </Button>
                      );
                    } else {
                      return (
                        <Button
                          variant="text"
                          size='small'
                          onClick={() => handleAction(displayHotel)}
                        >
                          {t('hotels.contentManager.detail.moderation.link.button')}
                        </Button>
                      );
                    }
                  },
                },
                {
                  id: 'id',
                  label: t('hotels.contentManager.fields.id'),
                  align: 'center',
                  canSort: true,
                  minWidth: 150,
                },
                { id: 'name', label: t('hotels.contentManager.fields.name'), align: 'left' },
                {
                  id: 'modParent',
                  label: t('hotels.contentManager.fields.modParent'),
                  align: 'center',
                  canSort: true,
                  minWidth: 150,
                },
                { id: 'modParentName', label: t('hotels.contentManager.fields.modParentName'), align: 'left' },
                {
                  id: 'supplierId',
                  label: t('hotels.contentManager.fields.accessCode'),
                  align: 'left',
                  maxWidth: 200,
                  format: (supplierId) => <>{findSupplier(supplierId)?.supplierName}</>,
                },
                { id: 'code', label: t('hotels.contentManager.fields.code'), align: 'left' },
                
                { id: 'address', label: t('hotels.contentManager.fields.address'), align: 'left' },
                { id: 'city', label: t('hotels.contentManager.fields.city'), align: 'left' },
                { id: 'country', label: t('hotels.contentManager.fields.country') },
              ]}
              data={{
                key: 'id',
                items: dupesData?.data || [],
                totalCount: dupesData?.data.length || 0,
              }}
            />
          )}
        </Box>
      </StyledBox>
    </Modal>
  );
};
